import React, { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Sparkles,
  ListOrdered,
  Shirt,
  FileText,
  Handshake,
  LogOut,
  Menu,
} from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import { Sheet, SheetContent, SheetTrigger } from 'components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { AuthContext } from '../../context/AuthContext';

const NAV_GROUPS = [
  {
    label: 'Creator Hub',
    items: [
      { id: 'offer', label: 'Your Offer', icon: Sparkles, path: '/' },
      { id: 'referrals', label: 'Referral Centre', icon: Handshake, path: '/referrals' },
    ],
  },
  {
    label: 'Content',
    items: [
      { id: 'rankings', label: 'Draft Rankings', icon: ListOrdered, path: '/rankings' },
      { id: 'lineups', label: 'Predicted Lineups', icon: Shirt, path: '/lineups' },
      { id: 'blog', label: 'Blog', icon: FileText, path: '/blog' },
    ],
  },
];

function NavContent({ onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useContext(AuthContext);

  const handleClick = (path) => {
    navigate(path);
    onNavigate?.();
  };

  return (
    <div className="flex h-full flex-col bg-card">
      {/* Logo */}
      <div className="flex min-h-[64px] items-center px-5">
        <span className="text-token-lg font-bold text-secondary">DraftFC Creators</span>
      </div>

      <Separator />

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-3">
        {NAV_GROUPS.map((group) => (
          <div key={group.label} className="mb-4">
            <p className="mb-1 px-2 text-token-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {group.label}
            </p>
            {group.items.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  data-compact
                  onClick={() => handleClick(item.path)}
                  className={cn(
                    'mb-0.5 flex w-full items-center gap-3 rounded-token-medium px-2 py-2 text-left text-token-md transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-textSecondary hover:bg-muted hover:text-foreground'
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="truncate">{item.label}</span>
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      <Separator />

      {/* User footer */}
      <div className="p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-compact
              className="flex w-full items-center gap-2 rounded-token-medium p-2 text-left hover:bg-muted"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-token-md font-semibold text-secondary-foreground">
                {user?.user?.email?.[0]?.toUpperCase() || 'C'}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-token-md font-medium">
                  {user?.user?.display_name || user?.user?.email || 'Creator'}
                </span>
                <span className="block truncate text-token-sm text-muted-foreground">
                  Content Creator
                </span>
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-56">
            <DropdownMenuItem onClick={logout}>
              <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export default function CreatorLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="app-shell flex">
      {/* Desktop sidebar */}
      <aside className="hidden w-[260px] shrink-0 border-r bg-card md:block">
        <NavContent />
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <header className="flex items-center gap-2 border-b bg-secondary px-3 py-2 text-secondary-foreground md:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-secondary-foreground hover:bg-secondary/80 hover:text-secondary-foreground"
                aria-label="Open navigation"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[260px] p-0">
              <NavContent onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <span className="text-token-lg font-semibold">DraftFC Creators</span>
        </header>

        {/* Scrollable page content */}
        <main className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
          <div className="w-full max-w-full p-4 sm:p-6">{children}</div>
        </main>
      </div>
    </div>
  );
}
